"use client";

import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { usePathname, useRouter } from "next/navigation";
import type { SafeUser, UserRole } from "@/lib/users";
import { can } from "@/lib/users";
import { isPublicPath } from "@/lib/public-paths";
import { resolveNavigationForUser } from "@/lib/navigation/resolver";
import { readStoredExperiencePreferences } from "@/lib/navigation/preferences";
import { fetchNavigationPolicy } from "@/lib/navigation/policy-client";

export type LoginResult = {
  ok: boolean;
  error?: string;
  /** Password was correct; a TOTP/recovery code is now required
   *  (POST it via completeTwoFactor). No session exists yet. */
  twoFactorRequired?: boolean;
  /** Signed in on a setup-limited session — the user must enroll in 2FA
   *  at /two-factor-setup before anything else. */
  setupRequired?: boolean;
};

type AuthCtx = {
  user: SafeUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  /** Second step of a 2FA login: verify the code, mint the real session. */
  completeTwoFactor: (code: string) => Promise<{ ok: boolean; error?: string; code?: string }>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({
  children,
  /**
   * The caller, resolved on the server from the session cookie and handed to
   * us with the HTML. See `resolveSessionIdentity`.
   *
   * This is what lets the app render ITSELF on the first frame instead of a
   * loading splash. Before it, `user` started null and `loading` true, so
   * every authenticated page began as a full-screen brand mark and became the
   * application one fetch later — and every `useCan()` in the tree answered
   * "no" until that fetch landed, so permission-gated buttons, nav entries and
   * whole sections popped in afterwards. Both problems are the same problem:
   * the client was being asked a question the server had already answered.
   *
   * `undefined` means the server did not resolve it (it is optional so the
   * provider still works anywhere it is mounted without one), and the fetch
   * path below behaves exactly as it always did.
   */
  initialUser,
}: {
  children: React.ReactNode;
  initialUser?: SafeUser | null;
}) {
  const [user, setUser] = useState<SafeUser | null>(initialUser ?? null);
  // Only "loading" when the server did not already tell us. When it did,
  // there is nothing to wait for and nothing to flash.
  const [loading, setLoading] = useState(initialUser === undefined);
  const pathname = usePathname();
  const router = useRouter();

  const fetchMe = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/me", { cache: "no-store" });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // Already served with the page: no bootstrap probe. The client still
    // re-checks on every login, workspace switch and 2FA enrolment through
    // `reload()` — this only drops the redundant round trip on first paint.
    if (initialUser !== undefined) return;
    fetchMe();
    // `initialUser` is a render-time constant for the life of the document.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMe]);

  // Redirect logic
  useEffect(() => {
    if (loading) return;
    if (!user && !isPublicPath(pathname)) {
      router.replace("/login");
      return;
    }
    if (user && pathname === "/login") {
      // Land on the user's resolved start page rather than a hard-coded /app.
      // The resolver filters by the user's real permissions and clamps to the
      // admin policy, so the landing route is always one they can actually see
      // — never a route they lack access to. Policy fetch degrades to the
      // permissive default, so a flaky request can't strand the redirect.
      let cancelled = false;
      void (async () => {
        const policy = await fetchNavigationPolicy();
        const resolved = resolveNavigationForUser({
          role: user.role,
          platformAdmin: user.platformAdmin === true,
          policy,
          preferences: readStoredExperiencePreferences(),
        });
        if (!cancelled) router.replace(resolved.startPageHref || "/app");
      })();
      return () => { cancelled = true; };
    }
  }, [user, loading, pathname, router]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (res.ok && data.twoFactorRequired) {
        // Correct password, second factor pending — no session yet.
        return { ok: false, twoFactorRequired: true };
      }
      if (res.ok && data.user) {
        setUser(data.user);
        return { ok: true, setupRequired: data.twoFactorSetupRequired === true };
      }
      return { ok: false, error: data.error ?? "Login failed" };
    } catch {
      return { ok: false, error: "Network error" };
    }
  }, []);

  const completeTwoFactor = useCallback(async (code: string) => {
    try {
      const res = await fetch("/api/auth/two-factor/challenge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.user) {
        setUser(data.user);
        return { ok: true };
      }
      return { ok: false, error: data.error ?? "Verification failed", code: data.code as string | undefined };
    } catch {
      return { ok: false, error: "Network error" };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } catch { /* best-effort */ }
    setUser(null);
    router.replace("/login");
  }, [router]);

  return (
    <Ctx.Provider value={{ user, loading, login, completeTwoFactor, logout, reload: fetchMe }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

/** Check if current user can perform an action */
export function useCan(action: string): boolean {
  const { user } = useAuth();
  if (!user) return false;
  return can(user.role, action);
}
